import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Building2, Users, UserPlus, Crown, Shield, User, Eye, 
  ChevronRight, ChevronDown, Settings, BarChart3, Mail,
  Phone, Calendar, Target, TrendingUp, RefreshCw, Plus,
  Briefcase, UserCircle, Edit, Trash2, MoreVertical, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role configuration
const roleConfig = {
  super_admin: { name: 'Super Admin', icon: Crown, color: 'bg-red-500', level: 0 },
  account_owner: { name: 'Account Owner', icon: Crown, color: 'bg-amber-500', level: 1 },
  admin: { name: 'Admin', icon: Shield, color: 'bg-purple-500', level: 2 },
  manager: { name: 'Sales Manager', icon: Users, color: 'bg-blue-500', level: 3 },
  sales_manager: { name: 'Sales Manager', icon: Users, color: 'bg-blue-500', level: 3 },
  team_lead: { name: 'Team Lead', icon: Users, color: 'bg-cyan-500', level: 3 },
  sales_executive: { name: 'Sales Executive', icon: User, color: 'bg-indigo-500', level: 4 },
  telesales: { name: 'Telesales', icon: User, color: 'bg-orange-500', level: 4 },
  customer_service: { name: 'Customer Service', icon: User, color: 'bg-teal-500', level: 4 },
  sales_rep: { name: 'Sales Agent', icon: User, color: 'bg-green-500', level: 4 },
  viewer: { name: 'Viewer', icon: Eye, color: 'bg-slate-500', level: 5 }
};

const OrganizationStructure = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState({});
  
  // Dialogs
  const [showAddTeamDialog, setShowAddTeamDialog] = useState(false);
  const [showAddAgentDialog, setShowAddAgentDialog] = useState(false);
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] = useState(false);
  const [showDeleteAgentDialog, setShowDeleteAgentDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  
  // Forms
  const [newTeamName, setNewTeamName] = useState('');
  const [newAgent, setNewAgent] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'sales_rep',
    team_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchOrganizationData();
  }, [token]);

  const fetchOrganizationData = async () => {
    setLoading(true);
    try {
      // Fetch current user's account
      const accountRes = await axios.get(`${API}/accounts/current?token=${token}`);
      if (accountRes.data.account) {
        setAccount(accountRes.data.account);
      }
      
      // Fetch teams for account
      const teamsRes = await axios.get(`${API}/teams/list?token=${token}`);
      setTeams(teamsRes.data.teams || []);
      
      // Fetch all users for account
      const usersRes = await axios.get(`${API}/accounts/users?token=${token}`);
      setUsers(usersRes.data?.users || []);
      
    } catch (error) {
      console.error('Error fetching organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Team name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/teams/create?token=${token}`, {
        name: newTeamName
      });

      if (response.data.success) {
        toast.success('Team created successfully');
        setShowAddTeamDialog(false);
        setNewTeamName('');
        fetchOrganizationData();
      } else {
        toast.error(response.data.error || 'Failed to create team');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAgent = async () => {
    if (!newAgent.full_name || !newAgent.email || !newAgent.password) {
      toast.error('All fields are required');
      return;
    }

    setSaving(true);
    try {
      // First add user to account
      const response = await axios.post(`${API}/accounts/users?token=${token}`, {
        full_name: newAgent.full_name,
        email: newAgent.email,
        password: newAgent.password,
        role: newAgent.role
      });

      if (response.data.success) {
        // Then assign to team if specified
        if (newAgent.team_id && response.data.user?.id) {
          await axios.post(`${API}/teams/${newAgent.team_id}/members?token=${token}`, {
            user_id: response.data.user.id,
            role: newAgent.role
          });
        }

        toast.success('Sales agent added successfully');
        setShowAddAgentDialog(false);
        setNewAgent({ full_name: '', email: '', password: '', role: 'sales_rep', team_id: '' });
        fetchOrganizationData();
      } else {
        toast.error(response.data.error || 'Failed to add agent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/teams/${teamToDelete.id}?token=${token}`);
      
      if (response.data.success) {
        toast.success('Team deleted successfully');
        setShowDeleteTeamDialog(false);
        setTeamToDelete(null);
        fetchOrganizationData();
      } else {
        toast.error(response.data.error || 'Failed to delete team');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/accounts/users/${agentToDelete.id}?token=${token}`);
      
      if (response.data.success) {
        toast.success('Agent deleted successfully');
        setShowDeleteAgentDialog(false);
        setAgentToDelete(null);
        fetchOrganizationData();
      } else {
        toast.error(response.data.error || 'Failed to delete agent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteTeamDialog = (team, e) => {
    if (e) e.stopPropagation();
    setTeamToDelete(team);
    setShowDeleteTeamDialog(true);
  };

  const openDeleteAgentDialog = (agent, e) => {
    if (e) e.stopPropagation();
    setAgentToDelete(agent);
    setShowDeleteAgentDialog(true);
  };

  const getUsersForTeam = (teamId) => {
    return Array.isArray(users) ? users.filter(u => u.team_id === teamId) : [];
  };

  const getUnassignedUsers = () => {
    return Array.isArray(users) ? users.filter(u => !u.team_id) : [];
  };

  const getRoleConfig = (role) => {
    return roleConfig[role] || roleConfig.viewer;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      timeZone: 'Asia/Dubai',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="organization-structure-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Organization Structure</h1>
          <p className="text-slate-500 mt-1">
            Manage your account hierarchy: Account → Sales Teams → Sales Agents
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOrganizationData}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowAddAgentDialog(true)} className="bg-green-600 hover:bg-green-700">
            <UserPlus size={16} className="mr-2" />
            Add Agent
          </Button>
          <Button onClick={() => setShowAddTeamDialog(true)}>
            <Plus size={16} className="mr-2" />
            Add Team
          </Button>
        </div>
      </div>

      {/* Hierarchy Legend */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-sm text-slate-600">Account Owner</span>
            </div>
            <ChevronRight className="text-slate-300" size={20} />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm text-slate-600">Sales Team</span>
            </div>
            <ChevronRight className="text-slate-300" size={20} />
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm text-slate-600">Sales Agent</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Organization Tree */}
        <div className="col-span-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Building2 size={20} className="text-amber-500" />
                {account?.name || 'Your Organization'}
              </CardTitle>
              <CardDescription>
                Plan: <Badge variant="secondary" className="ml-1">{account?.plan?.toUpperCase() || 'STARTER'}</Badge>
                <span className="mx-2">•</span>
                {users.length} / {account?.user_limit || 5} Users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Account Owner */}
              {(Array.isArray(users) ? users : []).filter(u => u.is_account_owner || u.role === 'account_owner').map(owner => (
                <div key={owner.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Avatar className="h-10 w-10 bg-amber-500">
                    <AvatarFallback className="text-white font-medium">
                      {owner.full_name?.[0] || 'O'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{owner.full_name}</span>
                      <Badge className="bg-amber-500 text-white text-xs">Account Owner</Badge>
                    </div>
                    <span className="text-sm text-slate-500">{owner.email}</span>
                  </div>
                  <Crown className="text-amber-500" size={20} />
                </div>
              ))}

              {/* Teams */}
              <div className="space-y-3 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Users size={16} />
                  Sales Teams ({teams.length})
                </h3>

                {teams.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <Users className="mx-auto text-slate-300 mb-2" size={32} />
                    <p className="text-slate-500">No teams created yet</p>
                    <Button 
                      variant="link" 
                      className="text-blue-600"
                      onClick={() => setShowAddTeamDialog(true)}
                    >
                      Create your first team
                    </Button>
                  </div>
                ) : (
                  teams.map(team => (
                    <div key={team.id} className="border rounded-lg overflow-hidden">
                      {/* Team Header */}
                      <div 
                        className="flex items-center gap-3 p-3 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => toggleTeam(team.id)}
                      >
                        {expandedTeams[team.id] ? (
                          <ChevronDown size={18} className="text-blue-600" />
                        ) : (
                          <ChevronRight size={18} className="text-blue-600" />
                        )}
                        <div className="p-2 bg-blue-500 rounded-lg">
                          <Users size={16} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-slate-800">{team.name}</span>
                          <span className="text-sm text-slate-500 ml-2">
                            ({getUsersForTeam(team.id).length} agents)
                          </span>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-200 mr-2">
                          Sales Team
                        </Badge>
                        <button
                          onClick={(e) => openDeleteTeamDialog(team, e)}
                          className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete Team"
                          data-testid={`delete-team-${team.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Team Members */}
                      {expandedTeams[team.id] && (
                        <div className="border-t bg-white">
                          {getUsersForTeam(team.id).length === 0 ? (
                            <div className="p-4 text-center text-slate-500 text-sm">
                              No agents assigned to this team
                            </div>
                          ) : (
                            <div className="divide-y">
                              {getUsersForTeam(team.id).map(member => {
                                const roleConf = getRoleConfig(member.role);
                                const RoleIcon = roleConf.icon;
                                const isOwner = member.is_account_owner || member.role === 'account_owner';
                                return (
                                  <div key={member.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 group">
                                    <div className="w-6" /> {/* Indent */}
                                    <Avatar className={`h-8 w-8 ${roleConf.color}`}>
                                      <AvatarFallback className="text-white text-sm">
                                        {member.full_name?.[0] || 'U'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm text-slate-800">
                                          {member.full_name}
                                        </span>
                                        <Badge className={`${roleConf.color} text-white text-xs`}>
                                          {roleConf.name}
                                        </Badge>
                                      </div>
                                      <span className="text-xs text-slate-500">{member.email}</span>
                                    </div>
                                    <RoleIcon size={16} className="text-slate-400" />
                                    {!isOwner && (
                                      <button
                                        onClick={(e) => openDeleteAgentDialog(member, e)}
                                        className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                        title="Delete Agent"
                                        data-testid={`delete-agent-${member.id}`}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div className="p-2 bg-slate-50 border-t">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="w-full text-blue-600"
                              onClick={() => {
                                setSelectedTeam(team);
                                setNewAgent(prev => ({ ...prev, team_id: team.id }));
                                setShowAddAgentDialog(true);
                              }}
                            >
                              <UserPlus size={14} className="mr-1" />
                              Add Agent to Team
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Unassigned Users */}
              {getUnassignedUsers().length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                    <UserCircle size={16} />
                    Unassigned Users ({getUnassignedUsers().length})
                  </h3>
                  <div className="space-y-2">
                    {getUnassignedUsers().filter(u => !u.is_account_owner && u.role !== 'account_owner').map(unassigned => {
                      const roleConf = getRoleConfig(unassigned.role);
                      return (
                        <div key={unassigned.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-dashed border-slate-200 group">
                          <Avatar className={`h-8 w-8 ${roleConf.color}`}>
                            <AvatarFallback className="text-white text-sm">
                              {unassigned.full_name?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <span className="font-medium text-sm">{unassigned.full_name}</span>
                            <span className="text-xs text-slate-500 ml-2">{unassigned.email}</span>
                          </div>
                          <Badge variant="outline" className="text-slate-500">
                            Not in team
                          </Badge>
                          <button
                            onClick={(e) => openDeleteAgentDialog(unassigned, e)}
                            className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete Agent"
                            data-testid={`delete-unassigned-${unassigned.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stats Panel */}
        <div className="col-span-4 space-y-4">
          {/* Account Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Account Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Users</span>
                  <span className="font-medium">{users.length} / {account?.user_limit || 5}</span>
                </div>
                <Progress 
                  value={(users.length / (account?.user_limit || 5)) * 100} 
                  className="h-2"
                />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Teams</span>
                  <span className="font-medium">{teams.length}</span>
                </div>
              </div>
              <div className="pt-2 border-t">
                <div className="text-xs text-slate-500">Plan: {account?.plan?.toUpperCase()}</div>
                <div className="text-xs text-slate-500">Created: {formatDate(account?.created_at)}</div>
              </div>
            </CardContent>
          </Card>

          {/* Role Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Role Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(
                  users.reduce((acc, u) => {
                    const role = u.role || 'viewer';
                    acc[role] = (acc[role] || 0) + 1;
                    return acc;
                  }, {})
                ).sort((a, b) => (roleConfig[a[0]]?.level || 99) - (roleConfig[b[0]]?.level || 99)).map(([role, count]) => {
                  const conf = getRoleConfig(role);
                  const RoleIcon = conf.icon;
                  return (
                    <div key={role} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded ${conf.color}`}>
                          <RoleIcon size={12} className="text-white" />
                        </div>
                        <span className="text-sm text-slate-700">{conf.name}</span>
                      </div>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => setShowAddTeamDialog(true)}
              >
                <Plus size={16} className="mr-2" />
                Create New Team
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => setShowAddAgentDialog(true)}
              >
                <UserPlus size={16} className="mr-2" />
                Add Sales Agent
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Team Dialog */}
      <Dialog open={showAddTeamDialog} onOpenChange={setShowAddTeamDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Create Sales Team</DialogTitle>
            <DialogDescription>
              Create a new sales team to organize your agents
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                placeholder="e.g., Enterprise Sales, APAC Team"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTeamDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTeam} disabled={saving}>
              {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : null}
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Agent Dialog */}
      <Dialog open={showAddAgentDialog} onOpenChange={setShowAddAgentDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Add Sales Agent</DialogTitle>
            <DialogDescription>
              Add a new sales agent to your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Full Name</Label>
              <Input
                id="agent-name"
                placeholder="John Doe"
                value={newAgent.full_name}
                onChange={(e) => setNewAgent(prev => ({ ...prev, full_name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-email">Email</Label>
              <Input
                id="agent-email"
                type="email"
                placeholder="john@company.com"
                value={newAgent.email}
                onChange={(e) => setNewAgent(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-password">Password</Label>
              <Input
                id="agent-password"
                type="password"
                placeholder="Minimum 8 characters"
                value={newAgent.password}
                onChange={(e) => setNewAgent(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-role">Role</Label>
              <Select 
                value={newAgent.role} 
                onValueChange={(value) => setNewAgent(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Agent</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-team">Assign to Team (Optional)</Label>
              <Select 
                value={newAgent.team_id || "none"} 
                onValueChange={(value) => setNewAgent(prev => ({ ...prev, team_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No team</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAgentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAgent} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : null}
              Add Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Confirmation Dialog */}
      <Dialog open={showDeleteTeamDialog} onOpenChange={setShowDeleteTeamDialog}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Delete Team
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{teamToDelete?.name}"? All agents in this team will become unassigned. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteTeamDialog(false);
                setTeamToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteTeam}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <><RefreshCw className="animate-spin mr-2" size={14} /> Deleting...</>
              ) : (
                <><Trash2 size={14} className="mr-2" /> Delete Team</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Agent Confirmation Dialog */}
      <Dialog open={showDeleteAgentDialog} onOpenChange={setShowDeleteAgentDialog}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Delete Agent
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{agentToDelete?.full_name}" ({agentToDelete?.email})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteAgentDialog(false);
                setAgentToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAgent}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <><RefreshCw className="animate-spin mr-2" size={14} /> Deleting...</>
              ) : (
                <><Trash2 size={14} className="mr-2" /> Delete Agent</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationStructure;
